import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useGetConfigWebApiHandler_getConfig } from '@zg-rentals/ts-rent-payment-client-react-query';

import { QUERY_TAGS } from '../constants/QueryTagConstants';
import { paymentsQueryMeta } from '../utils/paymentsQuery';

interface FeatureFlags {
  enableScalablePaymentsView: boolean;
  enableTransactionTabRefresh: boolean;
}

interface FeatureFlagsContextType {
  featureFlags: FeatureFlags;
  updateFeatureFlags: (flags: Partial<FeatureFlags>) => void;
  setEnableScalablePaymentsView: (enabled: boolean) => void;
  setEnableTransactionTabRefresh: (enabled: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    enableScalablePaymentsView: false,
    enableTransactionTabRefresh: false,
  });

  const { data: configData } = useGetConfigWebApiHandler_getConfig(
    {},
    { meta: paymentsQueryMeta('GetConfigWebApiHandler_getConfig', 'feature flags', [QUERY_TAGS.GET_CONFIG]) },
  );

  useEffect(() => {
    if (configData) {
      setFeatureFlags((prev) => ({
        ...prev,
        enableTransactionTabRefresh: configData.enableTransactionTabRefresh,
      }));
    }
  }, [configData]);

  const updateFeatureFlags = useCallback((newFlags: Partial<FeatureFlags>) => {
    setFeatureFlags((prev) => ({
      ...prev,
      ...newFlags,
    }));
  }, []);

  const setEnableScalablePaymentsView = useCallback((enabled: boolean) => {
    setFeatureFlags((prev) => ({
      ...prev,
      enableScalablePaymentsView: enabled,
    }));
  }, []);

  const setEnableTransactionTabRefresh = useCallback((enabled: boolean) => {
    setFeatureFlags((prev) => ({
      ...prev,
      enableTransactionTabRefresh: enabled,
    }));
  }, []);

  const value: FeatureFlagsContextType = {
    featureFlags,
    updateFeatureFlags,
    setEnableScalablePaymentsView,
    setEnableTransactionTabRefresh,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const useScalablePaymentsView = (): boolean => {
  const { featureFlags } = useFeatureFlags();
  return featureFlags.enableScalablePaymentsView;
};

export const useTransactionTabRefresh = (): boolean => {
  const { featureFlags } = useFeatureFlags();
  return featureFlags.enableTransactionTabRefresh;
};
